import { callLambda } from './lambda.js';
import { parseLedgerEstimate, type LedgerEstimate } from './ledger-estimate.js';

// What public-ledger-estimate is asked (stage 9): a workflow before it starts, a running workflow (whole or from a node),
// one job card, or the recover-keys card's paid lookup (OQ13). The server checks the caller may see it.
export type LedgerEstimateRequest =
  | { companyId: string; workflowName: string; workflowVersion?: string; entry?: string }
  | { workflowId: string; fromNode?: string }
  | { jobId: string; outcome?: string };

export const ESTIMATE_CACHE_MS = 5 * 60 * 1000;

/** Whether an estimate fetched at `at` is to be asked for again: none yet, or older than the five-minute cache. */
export function estimateIsStale(at: number | undefined, now: number, ttlMs = ESTIMATE_CACHE_MS): boolean {
  return at === undefined || now - at >= ttlMs;
}

/** What of a company an estimate depends on (its public ledger state): estimates are dropped when this changes. */
export function ledgerStateKey(company: { ledgerCred?: unknown } | null | undefined): string {
  return JSON.stringify(company?.ledgerCred ?? null);
}

interface Cached {
  at: number;
  value: Promise<LedgerEstimate | null>;
}

/**
 * Estimates by request, kept for five minutes: ledger fees follow the HBAR price, so an estimate is asked for again after
 * that. `drop` forgets the ones a realtime change makes stale (the company's ledger state, a job's status). A refused or
 * failed request isn't kept.
 */
export class LedgerEstimateCache {
  private readonly entries = new Map<string, Cached>();

  constructor(
    private readonly fetchEstimate: (request: LedgerEstimateRequest) => Promise<LedgerEstimate | null>,
    private readonly now: () => number = () => Date.now(),
    private readonly ttlMs = ESTIMATE_CACHE_MS,
  ) {}

  static key(request: LedgerEstimateRequest): string {
    return JSON.stringify(Object.entries(request).sort(([a], [b]) => a.localeCompare(b)));
  }

  get(request: LedgerEstimateRequest): Promise<LedgerEstimate | null> {
    const key = LedgerEstimateCache.key(request);
    const cached = this.entries.get(key);
    if (cached && this.now() - cached.at < this.ttlMs) return cached.value;

    const value = this.fetchEstimate(request);
    this.entries.set(key, { at: this.now(), value });
    value.catch(() => {
      if (this.entries.get(key)?.value === value) this.entries.delete(key);
    });
    return value;
  }

  // Forgets every estimate whose request matches (all of them with no test)
  drop(matches: (request: LedgerEstimateRequest) => boolean = () => true) {
    for (const key of [...this.entries.keys()]) {
      const request = Object.fromEntries(JSON.parse(key) as [string, string][]) as LedgerEstimateRequest;
      if (matches(request)) this.entries.delete(key);
    }
  }
}

/** Asks public-ledger-estimate. Reads only: it never spends or writes. A refusal throws with the lambda's message. */
export async function requestLedgerEstimate(
  token: string | undefined,
  request: LedgerEstimateRequest,
): Promise<LedgerEstimate | null> {
  const result = await callLambda('public-ledger/estimate', 'POST', token, JSON.stringify(request));
  if (result.res.code !== 200) {
    throw new Error(
      typeof result.res.message === 'string' && result.res.message ? result.res.message : 'Unable to estimate',
    );
  }
  return parseLedgerEstimate(result.res.message);
}
